import { KatalComponent } from '../../shared/base';

/** The valid types for the type attribute */
const VALID_TYPES = ['default', 'success', 'warning', 'info'];

/**
 * @component {kat-badge} KatalBadge Badges highlight the status of an entity for quick recognition
 * @status Production
 * @theme flo
 * @guideline Do Badges can only contain alphanumeric values.
 * @guideline Do Badges are always color-coded and positioned next to the object they are informing
 * @guideline Dont Do not include icons or imagery within the badge.
 * @guideline Dont Do not use badges to indicate status.
 * @example Success {"label": "Success Badge", "type":"success"}
 * @example Info {"label": "Informational Badge", "type":"info"}
 * @example Default {"label": "Default Badge", "type":"default"}
 * @example Warning {"label": "Warning Badge", "type":"warning"}
 * @example Angled {"label": "Angled Badge", "type":"default", "variant": "angled"}
 */
export class KatalBadge extends KatalComponent {
  static get observedAttributes() {
    return ['label', 'type'];
  }

  constructor() {
    super(KatalBadge.observedAttributes);
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  /**
   * @param {string} label This is the text that will be displayed to the user
   * @required
   */
  get label() {
    return this.getAttribute('label') || '';
  }

  set label(value) {
    this.setAttribute('label', value);
  }

  /**
   * @param {enum} type The type of badge to display to the user
   * @enum {value} default Used for a generic status or state
   * @enum {value} success Items that have positive meaning
   * @enum {value} warning Items with problems or that have been removed
   * @enum {value} info Items that are actionable
   */
  get type() {
    let type = this.getAttribute('type') || 'default';
    if (!VALID_TYPES.includes(type.toLowerCase())) {
      type = 'default';
    }
    return type.toLowerCase();
  }

  set type(value) {
    if (VALID_TYPES.includes(value.toLowerCase())) {
      this.setAttribute('type', value);
    }
  }

  /**
   * @param {enum} variant The style of badge to display to users
   * @enum {value} default The default state for a badge
   * @enum {value} angled Badge that fits the corner of an element
   */
  get variant() {
    return this.getAttribute('variant') || 'default';
  }

  set variant(value) {
    this.setAttribute('variant', value);
  }

  render() {
    this.innerHTML = '';
    const container = document.createElement('span');
    container.innerText = this.label;
    container.classList.add(this.type);
    this.appendChild(container);
  }

  // Respond to attribute changes.
  attributeChangedCallback() {
    this.render();
  }
}

KatalComponent.register('kat-badge', KatalBadge);
